import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GitHubMark } from '@shared/brand';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { Button } from '@shared/ui/button';
import { redirectToOAuth } from '../oauth';
import { selectOAuthProviders, selectOAuthProvidersLoaded } from '../store/selectors';
import { fetchOAuthProviders } from '../store/thunks';

/**
 * Monochrome brand marks: lucide ships no Google glyph and the design system
 * forbids brand hues, so both marks are inline `currentColor` SVGs (same
 * approach as BrandLogo in @shared/brand). The Button primitive auto-sizes
 * direct SVG children to size-4. GitHubMark moved to @shared/brand (the
 * marketing footer uses it too); re-exported here for compatibility.
 */
export const GoogleMark = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
  </svg>
);

export { GitHubMark };

/**
 * "Continue with Google / GitHub" block for the Login and Register cards.
 * Renders nothing until the capability probe answers, and nothing at all when
 * no provider is configured (self-host installs see no dead UI).
 */
export const OAuthButtons = () => {
  const dispatch = useAppDispatch();
  const providers = useAppSelector(selectOAuthProviders);
  const providersLoaded = useAppSelector(selectOAuthProvidersLoaded);
  const { t } = useTranslation('auth');

  useEffect(() => {
    if (!providersLoaded) {
      void dispatch(fetchOAuthProviders());
    }
  }, [dispatch, providersLoaded]);

  if (!providersLoaded || (!providers.google && !providers.github)) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        <span className="text-muted-foreground text-xs font-medium uppercase tracking-widest">
          {t('oauth.divider')}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
      {providers.google ? (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => redirectToOAuth('google')}
        >
          <GoogleMark />
          {t('oauth.google')}
        </Button>
      ) : null}
      {providers.github ? (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => redirectToOAuth('github')}
        >
          <GitHubMark />
          {t('oauth.github')}
        </Button>
      ) : null}
    </div>
  );
};
